import { Instance, SnapshotIn } from 'mobx-state-tree';
import OrganizationalGroup from '@stores/OrganizationalGroup';
import OrganizationalService from '@stores/OrganizationalService';

// This is now dynamically calculated based on available groups
// Keeping for backward compatibility but should be replaced with getDefaultGroupId function
export const DEFAULT_GROUP_ID = 1;

export type Service = SnapshotIn<typeof OrganizationalService>;

export function getDefaultGroup(organizationalGroups: Map<string, Instance<typeof OrganizationalGroup>>): Instance<typeof OrganizationalGroup> | null {
  if (organizationalGroups.size === 0) return null;

  // Get the group with the minimum ID
  const minId = Math.min(...Array.from(organizationalGroups.keys()).map(Number));
  return organizationalGroups.get(`${minId}`) || null;
}

export function getGroupById(
  groupId: number | string,
  organizationalGroups: Map<string, Instance<typeof OrganizationalGroup>>
): Instance<typeof OrganizationalGroup> | null {
  return organizationalGroups.get(String(groupId)) || null;
}

export function getGroupsByIds(
  groupIds: number[],
  organizationalGroups: Map<string, Instance<typeof OrganizationalGroup>>
): Instance<typeof OrganizationalGroup>[] {
  return groupIds
    .map(groupId => organizationalGroups.get(`${groupId}`))
    .filter((group): group is Instance<typeof OrganizationalGroup> => group !== undefined);
}

export function showFirstError(errors: Record<string, any>, showAlert: (message: string, type: string) => void): void {
  const errorKeys = Object.keys(errors);
  if (errorKeys.length > 0) {
    showAlert(errors[errorKeys[0]], 'danger');
  }
}

export function getAvailableGroupsForOrganization(
  organization: any,
  organizationalGroups: Map<string, Instance<typeof OrganizationalGroup>>
): Instance<typeof OrganizationalGroup>[] {
  const orgGroupIds = (organization?.organizationalGroups || []).map(
    (group: any) => typeof group === 'object' && group !== null ? group.id : group
  );
  return getGroupsByIds(orgGroupIds, organizationalGroups);
}

export function isGroupsLoading(organizationalGroups: Map<string, Instance<typeof OrganizationalGroup>>): boolean {
  return organizationalGroups.size === 0;
}

export function transformServiceToPlainObject(service: Instance<typeof OrganizationalService>): Service {
  return {
    id: service.id,
    name: service.name,
    description: service.description,
    createdBy:
      typeof service.createdBy === 'string'
        ? service.createdBy
        : service.createdBy?.name || '',
    createdAt:
      typeof service.createdAt === 'string'
        ? service.createdAt
        : service.createdAt?.toISOString?.() || '',
  };
}

export function getAvailableServices(organizationalServices: Map<string, Instance<typeof OrganizationalService>>): Service[] {
  return Array.from(organizationalServices.values()).map(transformServiceToPlainObject);
}

export function isServicesLoading(organizationalServices: Map<string, Instance<typeof OrganizationalService>>): boolean {
  return organizationalServices.size === 0;
}

export function getDefaultService(organizationalServices: Map<string, Instance<typeof OrganizationalService>>): Service | null {
  const firstService = Array.from(organizationalServices.values())[0];
  return firstService ? transformServiceToPlainObject(firstService) : null;
}

export function isDefaultGroup(group: Instance<typeof OrganizationalGroup>): boolean {
  return group.id === DEFAULT_GROUP_ID;
}

export function getOrganizationWithGroups(
  result: any,
  existingOrg: any,
  groupIds?: number[],
  defaultOrgGroupId?: number
): any {
  let organizationalGroups: any[] = [DEFAULT_GROUP_ID];

  if (result.organizationalGroups && result.organizationalGroups.length > 0) {
    organizationalGroups = result.organizationalGroups.map((group: any) => group.id || group);
  } else if (existingOrg?.organizationalGroups && existingOrg.organizationalGroups.length > 0) {
    organizationalGroups = existingOrg.organizationalGroups.map((group: any) => group.id || group);
  } else if (groupIds && groupIds.length > 0) {
    organizationalGroups = groupIds;
  }

  return {
    ...result,
    organizationalGroups,
    defaultOrgGroupId: Number(defaultOrgGroupId) || Number(existingOrg?.defaultOrgGroupId) || DEFAULT_GROUP_ID,
  };
}

export function getDefaultGroupId(organization: any): number {
  const val = organization?.defaultOrgGroupId;
  if (typeof val === 'object' && val !== null) return Number(val.id);
  return Number(val) || DEFAULT_GROUP_ID;
}

export function getDefaultGroupIdFromGroups(organizationalGroups: Map<string, Instance<typeof OrganizationalGroup>>): number {
  if (organizationalGroups.size === 0) return DEFAULT_GROUP_ID;

  // Get the group with the minimum ID
  const minId = Math.min(...Array.from(organizationalGroups.keys()).map(Number));
  return minId;
} 